import { hashCanonicalJson } from '../reportGeneration/reportContractHash';
/**
 * PSR Phase 2 — cache-aware tier-1 sentiment fan-out (shared by pipeline + standalone).
 */
import { getCached, setCached } from './sentimentCache';
import { buildCitationRefsFromFetchResults } from './citationRefs';
import {
  SENTIMENT_FETCHERS,
  type SentimentFetchOptions,
  type SentimentFetchResult,
  type SentimentFetcher,
  type SentimentFetcherSourceId,
} from '../../services/sentimentFetchers';
import {
  raceWithAbort,
  rethrowIfAborted,
  throwIfAborted,
} from '../abortSignal';

export interface Tier1SentimentBundle {
  results: SentimentFetchResult[];
  citationRefs: ReturnType<typeof buildCitationRefsFromFetchResults>;
  sourcesAttempted: SentimentFetcherSourceId[];
  fetchedAt: string;
  cacheHits: number;
  cacheMisses: number;
}

export interface FetchTier1SentimentArgs {
  siteId: string;
  opts?: SentimentFetchOptions;
  /** Test seam — inject mock fetchers. */
  fetchers?: readonly SentimentFetcher[];
}

async function fetchOneWithCache(
  siteId: string,
  fetcher: SentimentFetcher,
  opts: SentimentFetchOptions | undefined,
  mode: string,
): Promise<{ result: SentimentFetchResult; hit: boolean }> {
  throwIfAborted(opts?.signal);
  if (!opts?.skipCache) {
    const cached = await raceWithAbort(
      getCached(siteId, fetcher.sourceId, mode),
      opts?.signal,
    );
    throwIfAborted(opts?.signal);
    if (cached) return { result: cached, hit: true };
  }

  let result: SentimentFetchResult;
  try {
    const available = await raceWithAbort(fetcher.isAvailable(siteId, opts), opts?.signal);
    throwIfAborted(opts?.signal);
    if (!available) {
      result = {
        sourceId: fetcher.sourceId,
        coverage: 'none',
        normalizedFacts: [],
        citationRefs: [],
        errors: [`${fetcher.sourceId}: not available for site`],
        fetchedAt: new Date().toISOString(),
      };
    } else {
      result = await raceWithAbort(fetcher.fetch(siteId, opts), opts?.signal);
      throwIfAborted(opts?.signal);
    }
  } catch (e) {
    rethrowIfAborted(opts?.signal, e);
    result = {
      sourceId: fetcher.sourceId,
      coverage: 'none',
      normalizedFacts: [],
      citationRefs: [],
      errors: [`${fetcher.sourceId}: ${e instanceof Error ? e.message : String(e)}`],
      fetchedAt: new Date().toISOString(),
    };
  }

  if (result.coverage !== 'none' && !opts?.skipCache) {
    throwIfAborted(opts?.signal);
    await raceWithAbort(
      setCached(siteId, fetcher.sourceId, result, mode),
      opts?.signal,
    );
    throwIfAborted(opts?.signal);
  }
  return { result, hit: false };
}

/**
 * Fan out all tier-1 fetchers with persistent cache. NEVER throws.
 */
export async function fetchTier1SentimentSources(
  args: FetchTier1SentimentArgs,
): Promise<Tier1SentimentBundle> {
  throwIfAborted(args.opts?.signal);
  const fetchers = args.fetchers ?? SENTIMENT_FETCHERS;
  const mode = `${args.opts?.mode ?? 'feasibility'}|${hashCanonicalJson({ version: 2, fingerprint: args.opts?.queryFingerprint ?? null, windowDays: args.opts?.trendWindowDays ?? 365, lat: args.opts?.lat ?? null, lng: args.opts?.lng ?? null, state: args.opts?.state ?? null, county: args.opts?.county ?? null, locality: args.opts?.locality ?? null, technologyTerms: args.opts?.technologyTerms ?? null })}`;
  const tasks = fetchers.map(f => fetchOneWithCache(args.siteId, f, args.opts, mode));
  const settled = await raceWithAbort(Promise.all(tasks), args.opts?.signal);
  throwIfAborted(args.opts?.signal);

  let cacheHits = 0;
  let cacheMisses = 0;
  const results: SentimentFetchResult[] = [];
  for (const { result, hit } of settled) {
    results.push(result);
    if (hit) cacheHits += 1;
    else cacheMisses += 1;
  }

  return {
    results,
    citationRefs: buildCitationRefsFromFetchResults(results),
    sourcesAttempted: fetchers.map(f => f.sourceId),
    fetchedAt: new Date().toISOString(),
    cacheHits,
    cacheMisses,
  };
}
